import { useMemo } from 'react';
import isFunction from 'lodash/isFunction';
import classNames from 'classnames';

import { ArrowsImage } from '../arrows-image/arrows-image';
import { CardGrid } from '../card-grid/card-grid';
import {
  ColumnContainerTitle,
  ColumnContainerTitleType,
} from '../columns/column-container/column-container-title/column-container-title';
import { GridProps } from '../grid/grid';
import { Text } from '../text/text.component';
import { getModels, useChildComponents } from '../../utils/use-child-components';

import { CardGalleryProps, CardGalleryType } from './card-gallery.config';

const getSixCardProps = (index: number) => {
  const nonMobileWidth = index > 2 ? 4 : 6;

  return {
    mobile: 12,
    tablet: index === 0 ? 12 : nonMobileWidth,
    smallDesktop: index === 0 ? 12 : nonMobileWidth,
    desktop: index === 0 ? 12 : nonMobileWidth,
  };
};

const gridItemProps: {
  [T in CardGalleryType]: (index: number) => {
    mobile: GridProps['mobile'];
    tablet: GridProps['tablet'];
    smallDesktop: GridProps['smallDesktop'];
    desktop: GridProps['desktop'];
  };
} = {
  [CardGalleryType.THREE_CARDS]: (index) => ({
    mobile: 12,
    tablet: 12,
    smallDesktop: index === 0 ? 12 : 6,
    desktop: index === 0 ? 12 : 6,
  }),
  [CardGalleryType.FOUR_CARDS]: (index) => ({
    mobile: 12,
    tablet: index === 0 ? 12 : 6,
    smallDesktop: index === 0 ? 12 : 4,
    desktop: index === 0 ? 12 : 4,
  }),
  [CardGalleryType.FIVE_CARDS]: (index) => ({
    mobile: 12,
    tablet: index > 1 ? 6 : 12,
    smallDesktop: index > 1 ? 4 : 6,
    desktop: index > 1 ? 4 : 6,
  }),
  [CardGalleryType.SIX_CARDS]: (index) => getSixCardProps(index),
  [CardGalleryType.SEVEN_CARDS]: (index) => ({
    mobile: 12,
    tablet: index === 0 ? 12 : 6,
    smallDesktop: index === 0 ? 12 : 4,
    desktop: index === 0 ? 12 : 4,
  }),
  [CardGalleryType.FOUR_XX_SMALL_CARDS]: () => ({
    mobile: 12,
    tablet: 6,
    smallDesktop: 3,
    desktop: 3,
  }),
  [CardGalleryType.THREE_X_SMALL_CARDS]: () => ({
    mobile: 12,
    tablet: 6,
    smallDesktop: 4,
    desktop: 4,
  }),
  [CardGalleryType.TWO_MEDIUM_CARDS]: () => ({
    mobile: 12,
    tablet: 12,
    smallDesktop: 6,
    desktop: 6,
  }),
  [CardGalleryType.THREE_SMALL_CARDS]: () => ({
    mobile: 12,
    tablet: 6,
    smallDesktop: 4,
    desktop: 4,
  }),
};

export const getGridProps = ({ type, index }: { type?: CardGalleryType; index: number }) =>
  type && gridItemProps[type] && isFunction(gridItemProps[type]) ? gridItemProps[type](index) : {};

const CardGallery = ({
  additionalClasses,
  appliedCssClassNames,
  cqPath,
  isInEditor,
  showBrandGraphics,
  text,
  title,
  type,
  ...props
}: CardGalleryProps) => {
  const cardComponents = useChildComponents(
    cqPath,
    getModels({ cqPath, isInEditor, ...props }, ['card1', 'card2', 'card3', 'card4', 'card5', 'card6', 'card7']),
    { isInEditor }
  );

  const hasGradientBackground = useMemo(
    () => appliedCssClassNames === 'snowflake-card-gallery-blue-bg',
    [appliedCssClassNames]
  );

  return (
    <div data-testid="snowflake-card-gallery" className={classNames('snowflake-card-gallery', additionalClasses)}>
      {showBrandGraphics && <ArrowsImage />}
      {title && (
        <div className="snowflake-container">
          <ColumnContainerTitle
            title={title}
            marginBottom={!text ? ColumnContainerTitleType.LARGE : undefined}
            hasGradientBackground={hasGradientBackground}
            hasMidBlueBackground={false}
          />
        </div>
      )}
      <div className="snowflake-container">
        {text && (
          <div
            className={classNames('center', {
              'text-color-text-04-ui-bg-03': hasGradientBackground,
            })}
            data-testid="snowflake-card-gallery-text"
          >
            <Text {...text} />
          </div>
        )}
        <CardGrid getGridProps={(index) => getGridProps({ type, index })} items={cardComponents} cqPath={cqPath} />
      </div>
    </div>
  );
};

export default CardGallery;
